import React, { useState, useEffect } from "react";
import { apiAxios as axios } from "../../utils/apiClient.js";
import { Helmet } from "react-helmet";
import {
  Container,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  InputAdornment,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Box,
  Paper,
  Grid,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Error,
  Send,
  VerifiedUser,
  LockReset,
  PhoneIphone,
  Security,
} from "@mui/icons-material";
import { url } from "../../Constant/TextConstant";
import { useAuth } from "../../utils/AuthContext";
import Header from "../Header";
import Footer from "../Footer/Footer";
import { useUserLocation } from "../../utils/useUserLocation";

const reasons = [
  "Lost or Stolen Mobile",
  "Switching to a New Phone Number",
  "Changed Mobile Network/Carrier",
  "Personal Preference",
  "Moving to a Different Location/Country",
  "Security or Privacy Concerns",
  "Number Portability",
  "Account Management/Updating Information",
  "Fraud Prevention",
  "Technical Issues with Current Number",
  "Number Not Active",
  "Receiving Spam or Unwanted Messages",
  "Porting to a Business Number",
  "For Better Network Coverage",
  "Other",
];

const ChangePasswordMobile = () => {
  // Form state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [selectedReason, setSelectedReason] = useState(reasons[0]);
  const [customReason, setCustomReason] = useState("");

  // UI state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedAction, setSelectedAction] = useState("Change Password");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [waitingTime, setWaitingTime] = useState(0);
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [previousMobile, setPreviousMobile] = useState("");

  const baseAPIUrl = url.baseAPIUrl;
  const { currentUser, uniqueId } = useAuth();

  // Replace the existing location state with the useUserLocation hook
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });

    useEffect(() => {
        window.scrollTo(0, 0);
      }, []);
  // Update localStorage whenever selectedCategory changes
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  // Handler for modal close with location update
  const handleModalClose = (shouldClose, newCoordinates = null) => {
    setShowModal(false);
    if (newCoordinates) {
      // Update location through the hook's update function
      updateLocation({
        coordinates: {
          latitude: newCoordinates.latitude,
          longitude: newCoordinates.longitude,
        },
        address: newCoordinates.address || "",
        details: newCoordinates.details || null,
      });
    }
  };
  // Feedback state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    content: "",
  });

  // Timer for OTP resend
  useEffect(() => {
    let timer;
    if (waitingTime > 0) {
      timer = setTimeout(() => setWaitingTime(waitingTime - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [waitingTime]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showDialog = (title, content) => {
    setDialog({ open: true, title, content });
  };

  const closeDialog = () => {
    setDialog((prev) => ({ ...prev, open: false }));
  };

  const validatePassword = async () => {
    if (!currentPassword) {
      showSnackbar("Current password cannot be empty", "error");
      return false;
    }

    if (!newPassword || !confirmPassword) {
      showSnackbar("Please fill all password fields", "error");
      return false;
    }

    if (newPassword !== confirmPassword) {
      showSnackbar("Passwords do not match", "error");
      return false;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${baseAPIUrl}/api/LoginMaster/Login`, {
        UserName: currentUser || "",
        Password: currentPassword,
        Uniqueid: uniqueId || "",
      });

      if (response.status === 200) {
        return true;
      } else {
        showSnackbar("Incorrect current password", "error");
        return false;
      }
    } catch (error) {
      showSnackbar(`Error verifying password: ${error.message}`, "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!(await validatePassword())) return;

    try {
      setLoading(true);
      const response = await axios.post(
        `${baseAPIUrl}/api/LoginMaster/resetpassword`,
        {
          NewPassword: newPassword,
          Mobile: currentUser || "",
        }
      );

      if (response.status === 200) {
        showSnackbar("Password updated successfully!", "success");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        showSnackbar("Failed to update password", "error");
      }
    } catch (error) {
      showSnackbar(`Error updating password: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    if (waitingTime > 0) {
      showSnackbar(
        `Please wait ${waitingTime} seconds before requesting another OTP`,
        "info"
      );
      return;
    }

    if (!/^\d{10}$/.test(mobile)) {
      showSnackbar("Please enter a valid 10-digit mobile number", "error");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(
        `${baseAPIUrl}/api/LoginMaster/getotp?mobile=${mobile}`
      );

      if (response.status === 200) {
        const data = response.data;
        if (data.Message === "OTP sent successfully.") {
          setOtpSent(true);
          setOtpVerified(false);
          setPreviousMobile(mobile);

          // Set waiting time based on attempts
          const attempts = otpAttempts + 1;
          setOtpAttempts(attempts);

          let waitTime = 15;
          if (attempts === 2) waitTime = 30;
          else if (attempts === 3) waitTime = 60;
          else if (attempts > 3) waitTime = 300;

          setWaitingTime(waitTime);
          showSnackbar(`OTP sent to ${mobile}`, "success");
        } else {
          showSnackbar(data.Message, "error");
        }
      } else {
        showSnackbar("Failed to send OTP. Please try again.", "error");
      }
    } catch (error) {
      showSnackbar(`Error sending OTP: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = () => {
    // In a real app, you would verify against the OTP received from your backend
    if (otp.length < 4) {
      showSnackbar("Please enter a valid OTP", "error");
      return;
    }

    setOtpVerified(true);
    showSnackbar("Mobile number verified successfully!", "success");
  };

  const handleMobileChangeSubmit = () => {
    if (!otpVerified) {
      showSnackbar("Please verify your mobile number first", "error");
      return;
    }

    const requestData = {
      ID: 0,
      RequestType: "Change Mobile",
      Mobile: currentUser || "",
      Reason: selectedReason === "Other" ? customReason : selectedReason,
      Status: "Review",
      Date: new Date().toISOString().split("T")[0],
      NewMobile: mobile || "Null",
    };

    showDialog(
      "Request Submitted",
      "Your request has been submitted successfully. We will review it shortly."
    );
  };

  return (
    <>
      <Helmet>
        <title>Account Security Settings - Change Password/Mobile</title>
        <meta
          name="description"
          content="Manage your account security settings. Change your password or update your mobile number for better account security and access."
        />
        <meta
          name="keywords"
          content="account security, change password, update mobile number, account settings, security settings"
        />
        <meta
          property="og:title"
          content="Account Security Settings - Change Password/Mobile"
        />
        <meta
          property="og:description"
          content="Manage your account security settings. Change password or update mobile number."
        />
        <meta property="og:type" content="website" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      <Header
        userLocation={currentLocation} // Use currentLocation from hook
        setShowModal={setShowModal}
        setUserLocation={updateLocation} // Use updateLocation from hook
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card elevation={3} sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Box
            sx={{
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              p: 3,
              textAlign: "center",
            }}
          >
            <Security sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h5" component="h1" fontWeight="bold">
              Account Security Settings
            </Typography>
            <Typography variant="subtitle1">
              Manage your password and mobile number
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <FormControl component="fieldset" fullWidth sx={{ mb: 4 }}>
              <RadioGroup
                row
                value={selectedAction}
                onChange={(e) => setSelectedAction(e.target.value)}
                sx={{ justifyContent: "center", gap: 3 }}
              >
                <FormControlLabel
                  value="Change Password"
                  control={<Radio color="primary" />}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LockReset />
                      <span>Change Password</span>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="Change Mobile"
                  control={<Radio color="primary" />}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PhoneIphone />
                      <span>Change Mobile</span>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            {selectedAction === "Change Password" ? (
              <Grid container spacing={3}>
                <Grid item xs>
                  <TextField
                    fullWidth
                    label="Current Password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowCurrentPassword(!showCurrentPassword)
                            }
                            edge="end"
                          >
                            {showCurrentPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs>
                  <TextField
                    fullWidth
                    label="New Password"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handlePasswordChange}
                    disabled={loading}
                    size="large"
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <CheckCircle />
                      )
                    }
                  >
                    {loading ? "Updating..." : "Update Password"}
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs>
                  <TextField
                    fullWidth
                    label="New Mobile Number"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    variant="outlined"
                    disabled={otpVerified}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIphone
                            color={otpVerified ? "success" : "action"}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    onClick={sendOtp}
                    disabled={loading || waitingTime > 0 || otpVerified}
                    size="large"
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : waitingTime > 0 ? null : (
                        <Send />
                      )
                    }
                  >
                    {waitingTime > 0
                      ? `Resend OTP in ${waitingTime}s`
                      : "Send OTP"}
                  </Button>
                </Grid>

                {otpSent && (
                  <>
                    <Grid item xs>
                      <TextField
                        fullWidth
                        label="Enter OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        variant="outlined"
                        disabled={otpVerified}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <VerifiedUser
                                color={otpVerified ? "success" : "action"}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs>
                      <Button
                        fullWidth
                        variant="outlined"
                        color={otpVerified ? "success" : "secondary"}
                        onClick={verifyOtp}
                        disabled={otpVerified}
                        size="large"
                        startIcon={otpVerified ? <CheckCircle /> : null}
                      >
                        {otpVerified ? "Verified" : "Verify OTP"}
                      </Button>
                    </Grid>
                  </>
                )}

                <Grid item xs>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Reason for Changing Mobile Number
                  </Typography>
                  <Select
                    fullWidth
                    value={selectedReason}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    variant="outlined"
                  >
                    {reasons.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                {selectedReason === "Other" && (
                  <Grid item xs>
                    <TextField
                      fullWidth
                      label="Please specify your reason"
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      variant="outlined"
                      multiline
                      rows={3}
                    />
                  </Grid>
                )}

                <Grid item xs>
                  <Button
                    fullWidth
                    variant="contained"
                    color={otpVerified ? "success" : "primary"}
                    onClick={handleMobileChangeSubmit}
                    disabled={!otpVerified}
                    size="large"
                    startIcon={<VerifiedUser />}
                  >
                    Submit Change Request
                  </Button>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Feedback Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
            elevation={6}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Confirmation Dialog */}
        <Dialog
          open={dialog.open}
          onClose={closeDialog}
          PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
        >
          <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
            {dialog.title}
          </DialogTitle>
          <DialogContent>
            <Typography>{dialog.content}</Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
            <Button
              onClick={closeDialog}
              variant="contained"
              color="primary"
              sx={{ px: 4, borderRadius: 2 }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      <Footer />
    </>
  );
};

export default ChangePasswordMobile;
